import {
  Checkbox,
  DatePicker,
  Field,
  Select
} from '../../../../react-final-form-wrappers'
import FormRow from '../../FormRow'
import { client } from '../../../../client'
import Divider from '@material-ui/core/Divider'
import { Radios, TextField } from 'mui-rff'
import DisorderSection from '../DisorderSection/DisorderSection'
import { useField, useFormState } from 'react-final-form'
import Button from '@material-ui/core/Button'
import { AlertDialog } from '../../../../components/AlertDialog'
import LetterTemplate from './LetterTemplate'
import Alert from '@material-ui/lab/Alert'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { FilesSection } from '../FilesSection'

const StartTreatmentProgramSection = () => {
  const { t } = useTranslation('startTreatmentProgramSection')
  const {
    input: { value: programId }
  } = useField('id')

  const program = useFormState().values

  const missingFields = Object.values({
    primaryPsychologist: program?.primaryPsychologist
      ? ''
      : t('primaryPsychologist'),
    patientFirstName: program?.patient?.firstName ? '' : t('firstName'),
    patientLastName: program?.patient?.lastName ? '' : t('lastName'),
    birthDate: program?.patient?.birthDate ? '' : t('birthDate'),
    gender: program?.patient?.gender ? '' : t('gender'),
    street: program?.patient?.street ? '' : t('street'),
    postalCode: program?.patient?.postalCode ? '' : t('postalCode'),
    residence: program?.patient?.residence ? '' : t('residence'),
    houseNumber: program?.patient?.houseNumber ? '' : t('houseNumber'),
    bsn: program?.patient?.bsn ? '' : t('bsn'),
    startDate: program?.startDate ? '' : t('startDateLabel'),
    practice: program?.practice ? '' : t('practice'),
    language: program?.funnel?.language ? '' : t('language'),
    location: (program?.funnel?.online || program?.funnel?.onsite) ? '' : t('location')
  })
    .filter((field) => !!field) // filter fields that are not missing
    .join(', ')

  const [hoNOS, setHoNOS] = useState<any[]>()

  useEffect(() => {
    ; (async () => {
      const types = await client.getHoNOSTypes()

      const values = types.map((type: any) => {
        return {
          label: type.name === 'full' ? t('HoNOSFull') : t('HoNOSShort'),
          value: type.id.toString()
        }
      })
      setHoNOS(values)
    })()
  }, [])

  // Cross-field validation: warn when start date is before referral date
  const referralDate = useField('referralDate').input.value
  const isStartBeforeReferral = (() => {
    if (!program?.startDate || !referralDate) return false
    const s = new Date(program.startDate)
    s.setHours(0, 0, 0, 0)
    const r = new Date(referralDate)
    r.setHours(0, 0, 0, 0)
    return s < r
  })()

  return (
    <>
      <FormRow>
        <Field
          style={{ width: 300 }}
          name="startDate"
          label={t('startDateLabel')}
          component={DatePicker}
        />
      </FormRow>

      {isStartBeforeReferral && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          {t('referralBeforeStartWarning')}
        </Alert>
      )}

      <FormRow>
        <DisorderSection />
      </FormRow>

      <FormRow>
        <TextField
          multiline
          style={{ width: 600 }}
          name="disorderRemarks"
          label={t('disorderRemarks')}
        />
      </FormRow>

      <FormRow>
        <Select
          style={{ width: 600 }}
          label={t('productPerformance')}
          getOptions={async () => client.getProductOptions()}
          getOptionLabel={(i: any) => i.name}
          getOptionValue={(i: any) => i.id}
          name="productStart.id"
        />
      </FormRow>

      <FormRow>
        <Select
          style={{ width: 600 }}
          label={t('careDemandType')}
          getOptions={async () => client.getCareDemandTypes()}
          getOptionLabel={(i: any) => i.name}
          getOptionValue={(i: any) => i.id}
          name="careDemandType.id"
        />
      </FormRow>

      {hoNOS && (
        <FormRow>
          <Radios
            color="primary"
            label={t('HoNOSType')}
            name="hoNOSType"
            fieldProps={{
              initialValue:
                program?.hoNOSType?.id?.toString() ||
                program?.hoNOSType?.toString()
            }}
            formControlProps={{
              margin: 'none'
            }}
            radioGroupProps={{
              row: true
            }}
            data={hoNOS}
          />
        </FormRow>
      )}

      <FormRow>
        <Field
          label={t('referrerConsent')}
          name="referrerConsent"
          type="checkbox"
          component={Checkbox}
        />
      </FormRow>

      <FormRow>
        <Field
          label={t('specialistConsent')}
          name="specialistConsent"
          type="checkbox"
          component={Checkbox}
        />
      </FormRow>

      <FormRow>
        <Field
          label={t('startLetterSent')}
          name="startLetterSent"
          type="checkbox"
          component={Checkbox}
        />
      </FormRow>

      {(program?.practice?.name !== 'Mulder & Van Schie' ||
        !!program?.startIsiScore ||
        !!program?.startIsiSent) && (
        <>
          <FormRow>
            <Field
              name="startIsiSent"
              label={t('isiSent')}
              type="checkbox"
              component={Checkbox}
            />
          </FormRow>

          <FormRow>
            <TextField name="startIsiScore" label={t('isiScore')} />
          </FormRow>
        </>
      )}

      <Divider />

      <FormRow>
        <FilesSection
          typesToShow={['privacyStatement']}
          uploadedFileType={'privacyStatement'}
        />
      </FormRow>

      <FormRow>
        <FilesSection
          typesToShow={['treatmentAgreement']}
          uploadedFileType={'treatmentAgreement'}
        />
      </FormRow>

      <FormRow>
        <FilesSection
          typesToShow={['hoNOSResult']}
          uploadedFileType={'hoNOSResult'}
        />
      </FormRow>

      <FormRow>
        <FilesSection
          typesToShow={['startLetter']}
          uploadedFileType={'startLetter'}
        />
      </FormRow>

      {missingFields && (
        <Alert severity="warning">
          {t('missingFieldsWarning')} {missingFields}.
        </Alert>
      )}

      <AlertDialog
        fullWidth
        maxWidth={false}
        content={<LetterTemplate programId={programId} />}
        title={t('alertTitle')}
        confirmText={t('confirmText')}
        render={({ onClick }) => (
          <Button
            disabled={!!missingFields}
            onClick={onClick}
            type="submit"
            color="primary"
          >
            {t('alertTitle')}
          </Button>
        )}
      />
    </>
  )
}

export default StartTreatmentProgramSection
